import { AsyncPipe, DecimalPipe } from '@angular/common'
import { Component, inject, Input, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatFormField, MatInput, MatLabel } from '@angular/material/input'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { ChartConfiguration } from 'chart.js'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'
import { BaseChartDirective } from 'ng2-charts'
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  ReplaySubject,
  shareReplay,
  Subject,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs'
import { ButtonToggleComponent, ButtonToggleOption } from '../common/button-toggle/button-toggle.component'
import { convertIntervalKeyToTimeInterval, IntervalKey, TimeIntervalComponent } from '../common/time-interval/time-interval.component'
import { getActiveToggleMenuOptionIds, ToggleMenuComponent, ToggleMenuOptions } from '../common/toggle-menu/toggle-menu.component'
import { emitPaginationQueryParameterChanges, getPaginationOptionsFromQueryParamMap, constructQueryParameterName } from '../list-query-options'
import { CompletedCallDetails, MetricsService, UsageByOrganizationMetric, UsageByUserMetric } from '../metrics.service'
import { OrganizationsService } from '../organizations.service'
import { ColumnDefinition, PaginatedTableComponent, PaginatedTableEvent } from '../paginated-table/paginated-table.component'
import { SpinnerComponent } from '../spinner/spinner.component'
import { constructNameForUser, getColorPalette } from '../utils'
import { MatButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { ToastService } from '../toast.service'
import DataLabelsPlugin from 'chartjs-plugin-datalabels'
import { Plugin } from 'chart.js'

@Component({
  selector: 'app-analytics-dashboard',
  standalone: true,
  imports: [
    TimeIntervalComponent,
    AsyncPipe,
    TranslatePipe,
    BaseChartDirective,
    SpinnerComponent,
    PaginatedTableComponent,
    ButtonToggleComponent,
    ToggleMenuComponent,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatIconButtonSizesModule,
    TranslateModule,
    DecimalPipe,
    MatButton,
    MatIcon,
  ],
  templateUrl: './analytics-dashboard.component.html',
  styleUrl: './analytics-dashboard.component.scss',
})
export class AnalyticsDashboardComponent implements OnInit {
  @Input({ required: true })
  organizationIds$!: Observable<number[]>

  @Input()
  dashboardUrl: string | null = null

  @Input()
  showAudioMetrics = false

  dataLabelsPlugin: Plugin<'bar'> = DataLabelsPlugin as Plugin<'bar'>

  ngOnInit() {
    this.organizationIds$.subscribe((value) => this._organizationIds$.next(value))
    this.callNameQueryUpdate$.pipe(debounceTime(500)).subscribe((value) => this.updateCallNameQuery(value))
  }

  _organizationIds$ = new ReplaySubject<number[]>(1)
  route = inject(ActivatedRoute)
  router = inject(Router)
  organizationsService = inject(OrganizationsService)
  metricsService = inject(MetricsService)
  translateService = inject(TranslateService)
  completedCallQueryPrefix = 'completedCalls'
  usageStatsQueryPrefix = 'usageStats'
  defaultTimeInterval = 'LAST_6_MONTHS' as IntervalKey
  numberOfUserStatsEntriesToLoadInitially = 10
  callNameQueryUpdate$ = new Subject<string>()
  completedCallsDefaultPageSize = 25
  toastService = inject(ToastService)

  selectedInterval$ = this.route.queryParamMap.pipe(map((queryParamMap) => (queryParamMap.get('intervalKey') ?? this.defaultTimeInterval) as IntervalKey))
  completedCallsCallNameQuery$ = this.route.queryParamMap.pipe(
    map((queryParamMap) => queryParamMap.get(constructQueryParameterName('name', this.completedCallQueryPrefix)) ?? '')
  )

  interval$ = this.route.queryParamMap.pipe(
    map((params) => (params.get('intervalKey') as IntervalKey) ?? this.defaultTimeInterval),
    distinctUntilChanged(),
    map(convertIntervalKeyToTimeInterval),
    shareReplay(1)
  )

  usagesByOrganization$ = combineLatest([this._organizationIds$, this.interval$]).pipe(
    debounceTime(300),
    switchMap(([ids, interval]) => this.metricsService.getUsageByOrganizationMetrics(ids, interval.from)),
    shareReplay(1)
  )

  totalUsage$ = this.usagesByOrganization$.pipe(
    map((result) => {
      const organizations = result.data ?? []
      return organizations
        .flatMap((org: { usageByInterval: any }) => org.usageByInterval)
        .reduce(
          (acc: { duration: any; count: any }, interval: { duration: any; count: any }) => ({
            duration: acc.duration + (interval.duration ?? 0),
            count: acc.count + (interval.count ?? 0),
          }),
          { duration: 0, count: 0 }
        )
    })
  )
  userStats$ = this.route.queryParamMap.pipe(
    map((queryParamMap) => {
      const intervalKey = (queryParamMap.get('intervalKey') as IntervalKey) ?? this.defaultTimeInterval
      const interval = convertIntervalKeyToTimeInterval(intervalKey)
      const userStatsEntryCount = queryParamMap.get('userStatsEntryCount')
        ? parseInt(queryParamMap.get('userStatsEntryCount')!)
        : this.numberOfUserStatsEntriesToLoadInitially
      return { interval, userStatsEntryCount }
    }),
    withLatestFrom(this._organizationIds$),
    switchMap(([{ interval, userStatsEntryCount }, organizationIds]) =>
      this.metricsService.getUsageByUserMetrics('userStats', null, organizationIds, [], null, interval.from, userStatsEntryCount)
    )
  )

  activeUserCount$ = this.usagesByOrganization$.pipe(
    map((result) => {
      return result.data?.reduce((acc, organization) => acc + organization.activeUserCount, 0) ?? 0
    })
  )

  usageStatsSelectedView$ = this.route.queryParamMap.pipe(
    map((queryParamMap) => queryParamMap.get(constructQueryParameterName('activeView', this.usageStatsQueryPrefix)) ?? 'HOURS'),
    distinctUntilChanged(),
    tap((view) => console.log('Usage stats view changed to', view)),
    shareReplay(1)
  )

  columnDefinitionsForUserStats$ = combineLatest([this.userStats$, this.usageStatsSelectedView$, this._organizationIds$]).pipe(
    map(([result, viewMode, organizationIds]) => {
      const usageData = result.data?.list ?? []

      if (usageData.length === 0) return []

      const intervals = usageData[0].usageByInterval

      const base: ColumnDefinition<UsageByUserMetric>[] = [
        {
          columnDef: 'name',
          type: 'TEXT',
          header: 'COMMON.USER',
          cell: (metricDataPoint) => constructNameForUser(metricDataPoint.user),
          sortable: false,
          sticky: false,
        },
      ]

      const organization: ColumnDefinition<UsageByUserMetric> = {
        columnDef: 'organizationId',
        type: 'ORGANIZATION',
        header: 'COMMON.ORGANIZATION',
        cell: (metricDataPoint) => metricDataPoint.user.organization,
        sortable: false,
        sticky: false,
      }

      const intervalColumns = intervals.map(({ intervalLabel, intervalType }: any, index: number) => ({
        columnDef: `interval${index}`,
        type: viewMode === 'HOURS' ? 'NUMBER_WITH_ONE_DECIMAL' : 'NUMBER',
        header: () => this.formatIntervalLabel(intervalLabel, intervalType),
        cell: ({ usageByInterval }: UsageByUserMetric) => {
          const usage = usageByInterval[index]
          if (viewMode === 'HOURS') {
            const value = usage.duration
            return value === null ? 'N/A' : value === 0 ? '-' : value / 3600
          } else {
            const value = usage.count
            return value === null ? 'N/A' : value === 0 ? '-' : value
          }
        },
        sortable: false,
        sticky: false,
      }))

      const total: ColumnDefinition<UsageByUserMetric> = {
        columnDef: 'usageNumberTotal',
        type: viewMode === 'HOURS' ? 'NUMBER_WITH_ONE_DECIMAL' : 'NUMBER',
        header: 'COMMON.TOTAL',
        cell: (metricDataPoint) => {
          if (viewMode === 'HOURS') {
            const value = metricDataPoint.totalDuration
            return value === null ? 'N/A' : value === 0 ? '-' : value / 3600
          } else {
            const value = metricDataPoint.totalCount
            return value === null ? 'N/A' : value === 0 ? '-' : value
          }
        },
        sortable: false,
        sticky: false,
        highlight: true,
      }

      return [...base, ...(organizationIds.length > 1 ? [organization] : []), ...intervalColumns, total] as ColumnDefinition<UsageByUserMetric>[]
    })
  )

  exclusionFiltersForCompletedCalls$ = this.route.queryParamMap.pipe(
    map((queryParamMap) => {
      return {
        ERRORED: !(queryParamMap.get(constructQueryParameterName('exclusionFilters', this.completedCallQueryPrefix, 'ERRORED')) === 'false'),
        SHORTER_THAN_15_MIN:
          queryParamMap.get(constructQueryParameterName('exclusionFilters', this.completedCallQueryPrefix, 'SHORTER_THAN_15_MIN')) === 'true',
      }
    })
  )

  completedCalls$ = combineLatest([this._organizationIds$, this.route.queryParamMap, this.exclusionFiltersForCompletedCalls$]).pipe(
    debounceTime(300),
    switchMap(([organizationIds, queryParamMap, exclusionFiltersRaw]) => {
      const exclusionFilters = getActiveToggleMenuOptionIds(exclusionFiltersRaw)
      const activeViewQueryParam = constructQueryParameterName('activeView', this.completedCallQueryPrefix)
      const activeView = queryParamMap.has(activeViewQueryParam) ? queryParamMap.get(activeViewQueryParam) : null
      const interval = convertIntervalKeyToTimeInterval((queryParamMap.get('intervalKey') as IntervalKey) ?? this.defaultTimeInterval)
      const nameQueryParam = constructQueryParameterName('name', this.completedCallQueryPrefix)
      const callNameQuery = queryParamMap.has(nameQueryParam) ? queryParamMap.get(nameQueryParam) : null

      const { page, pageSize, sortField, sortDirection } = getPaginationOptionsFromQueryParamMap(queryParamMap, this.completedCallQueryPrefix, {
        pageSize: this.completedCallsDefaultPageSize,
        sortField: 'actualStartDate',
        sortDirection: 'desc',
      })

      return this.metricsService.getCompletedCalls(
        null,
        organizationIds,
        exclusionFilters,
        activeView,
        interval,
        callNameQuery,
        sortField,
        sortDirection ?? 'desc',
        page,
        pageSize
      )
    })
  )

  chartDataForOrganizationUsage$ = combineLatest([this.usagesByOrganization$, this.usageStatsSelectedView$]).pipe(
    map(([result, viewOption]) => {
      if (!result?.data) {
        return { labels: [], datasets: [] }
      }
      return this.transformToChartData(result.data, viewOption === 'HOURS' ? 'HOURS' : 'COUNTS')
    }),
    shareReplay(1)
  )

  barChartOptions$: Observable<ChartConfiguration<'bar'>['options']> = this._organizationIds$.pipe(
    map((organizationIds) => {
      const showLegend = organizationIds.length > 1

      return {
        responsive: true,
        layout: {
          padding: {
            top: 20,
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
          },
          y: { stacked: true },
        },
        plugins: {
          legend: {
            display: showLegend,
            position: 'bottom',
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const label = context.dataset.label ?? ''
                const value = context.raw
                return `${label}: ${Math.round(value as number)}`
              },
            },
          },
          datalabels: {
            anchor: 'end',
            align: 'top',
            color: '#000000',
            font: {
              weight: 'bold',
              size: 12,
            },
            formatter: (value: number, ctx) => {
              const datasets = ctx.chart.data.datasets
              let total = 0
              datasets.forEach((d) => {
                total += (d.data[ctx.dataIndex] as number) || 0
              })

              return ctx.datasetIndex === datasets.length - 1 && total > 0 ? total.toFixed(0) : ''
            },
          },
        },
      } satisfies ChartConfiguration<'bar'>['options']
    }),
    shareReplay(1)
  )

  fetchMoreUsers() {
    const currentNumberOfUserStats = this.route.snapshot.queryParamMap.has('userStatsEntryCount')
      ? parseInt(this.route.snapshot.queryParamMap.get('userStatsEntryCount')!)
      : this.numberOfUserStatsEntriesToLoadInitially
    this.router.navigate([], {
      queryParams: {
        userStatsEntryCount: currentNumberOfUserStats + 10,
      },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  usageStatsViewOptions: ButtonToggleOption[] = [
    {
      id: 'HOURS',
      labelTranslationKey: 'ADMIN.VIEW_OPTIONS.HOURS',
    },
    {
      id: 'COUNTS',
      labelTranslationKey: 'ADMIN.VIEW_OPTIONS.COUNTS',
    },
  ]

  updateSelectedView(value: string | null, prefix: string) {
    this.router.navigate([], {
      queryParams: {
        [constructQueryParameterName('activeView', prefix)]: value,
      },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  private baseColumnDefinitionsForCompletedCalls: ColumnDefinition<CompletedCallDetails>[] = [
    {
      columnDef: 'callId',
      type: 'NUMBER',
      header: 'COMMON.ID',
      cell: (metricDataPoint: CompletedCallDetails) => metricDataPoint.callId,
      sortable: true,
      sticky: false,
    },
    {
      columnDef: 'name',
      type: 'TEXT',
      header: 'COMMON.NAME',
      cell: (metricDataPoint: CompletedCallDetails) => metricDataPoint.name,
      sortable: true,
      sticky: false,
    },
    {
      columnDef: 'actualStartDate',
      type: 'DATE',
      header: 'COMMON.DATE_AND_TIME',
      cell: (metricDataPoint: CompletedCallDetails) => metricDataPoint.actualStartDate,
      sortable: true,
      sticky: false,
    },
    {
      columnDef: 'duration',
      type: 'DURATION',
      header: 'COMMON.DURATION',
      cell: (metricDataPoint: CompletedCallDetails) => metricDataPoint.duration,
      sortable: true,
      sticky: false,
    },
    {
      columnDef: 'callUsers',
      type: 'USER_AVATAR_LIST',
      header: 'COMMON.USERS',
      cell: (call: CompletedCallDetails) =>
        call.users.map((user) => {
          return { name: `${user.firstName} ${user.lastName}`, id: user.userId }
        }),
      sortable: false,
      sticky: false,
    },
  ]

  private audioMetricColumnDefinitions: ColumnDefinition<CompletedCallDetails>[] = [
    {
      columnDef: 'avgConfidenceScore',
      type: 'TEXT',
      header: 'METRICS.ANALYTICS_DASHBOARD.CONFIDENCE_SCORE',
      cell: (call: CompletedCallDetails) => (call.avgConfidenceScore !== null ? call.avgConfidenceScore.toFixed(2) : '-'),
      sortable: true,
      sticky: false,
    },
    {
      columnDef: 'avgRms',
      type: 'TEXT',
      header: 'METRICS.ANALYTICS_DASHBOARD.RMS',
      cell: (call: CompletedCallDetails) => (call.avgRms !== null ? call.avgRms.toFixed(2) : '-'),
      sortable: true,
      sticky: false,
    },
    {
      columnDef: 'avgHfRatio',
      type: 'TEXT',
      header: 'METRICS.ANALYTICS_DASHBOARD.HF_RATIO',
      cell: (call: CompletedCallDetails) => (call.avgHfRatio !== null ? call.avgHfRatio.toFixed(2) : '-'),
      sortable: true,
      sticky: false,
    },
  ]

  private statusColumnDefinition: ColumnDefinition<CompletedCallDetails> = {
    columnDef: 'status',
    type: 'COMPLETED_CALL_STATUS',
    header: 'COMMON.STATUS',
    cell: (call: CompletedCallDetails) => ({
      status: call.status,
      latestCallSessionStatus: call.latestCallSessionStatus,
      processingStatus: call.processingStatus,
    }),
    sortable: true,
    sticky: false,
  }

  get columnDefinitionsForCompletedCalls(): ColumnDefinition<CompletedCallDetails>[] {
    return [...this.baseColumnDefinitionsForCompletedCalls, ...(this.showAudioMetrics ? this.audioMetricColumnDefinitions : []), this.statusColumnDefinition]
  }

  async updateCallNameQuery(value: string) {
    await this.router.navigate([], {
      queryParams: {
        [constructQueryParameterName('name', this.completedCallQueryPrefix)]: value,
      },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  transformToChartData(usages: UsageByOrganizationMetric[], viewOption: 'HOURS' | 'COUNTS'): ChartConfiguration<'bar'>['data'] {
    const labels =
      usages[0]?.usageByInterval.map((usage) => {
        return this.formatIntervalLabel(usage.intervalLabel, usage.intervalType)
      }) ?? []
    const colorPalette = getColorPalette(usages.length)

    const datasets = usages.map((usage, index) => ({
      label: usage.organization.name,
      data: usage.usageByInterval.map((item) => (viewOption === 'HOURS' && item.duration !== null ? item.duration / 3600 : item.count)),
      backgroundColor: colorPalette[index],
    }))
    return {
      labels,
      datasets,
    }
  }

  updateIntervalKey(intervalKey: IntervalKey) {
    this.router.navigate([], {
      queryParams: { ['intervalKey']: intervalKey },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  handleListOptionsChangeForCompletedCalls(event: PaginatedTableEvent) {
    emitPaginationQueryParameterChanges(this.router, this.route, event, this.completedCallQueryPrefix)
  }

  updateCompletedCallsIntervalKey(intervalKey: IntervalKey) {
    this.router.navigate([], {
      queryParams: {
        [constructQueryParameterName('intervalKey', this.completedCallQueryPrefix)]: intervalKey,
      },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  updateCompletedCallsExclusionFilters(exclusionFilters: ToggleMenuOptions) {
    const queryParams = Object.entries(exclusionFilters).reduce((acc, [key, value]) => {
      return {
        ...acc,
        [constructQueryParameterName('exclusionFilters', this.completedCallQueryPrefix, key)]: value,
      }
    }, {})

    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  formatIntervalLabel(intervalLabel: string, intervalType: 'DAY' | 'MONTH'): string {
    if (intervalType === 'MONTH') {
      // intervalLabel is "YYYY-MM"
      const date = new Date(`${intervalLabel}-01`)
      const month = date.toLocaleString('en-US', { month: 'long' }).toUpperCase() // "SEPTEMBER"
      return this.translateService.instant(`COMMON.MONTHS.${month}`)
    }

    if (intervalType === 'DAY') {
      // intervalLabel is "YYYY-MM-DD"
      const date = new Date(intervalLabel)
      return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
      }).format(date)
    }

    return intervalLabel
  }

  exportCompletedCalls() {
    combineLatest([this._organizationIds$, this.route.queryParamMap, this.exclusionFiltersForCompletedCalls$])
      .pipe(
        take(1),
        switchMap(([organizationIds, queryParamMap, exclusionFiltersRaw]) => {
          const exclusionFilters = getActiveToggleMenuOptionIds(exclusionFiltersRaw)
          const activeViewQueryParam = constructQueryParameterName('activeView', this.completedCallQueryPrefix)
          const activeView = queryParamMap.has(activeViewQueryParam) ? queryParamMap.get(activeViewQueryParam) : null
          const interval = convertIntervalKeyToTimeInterval((queryParamMap.get('intervalKey') as IntervalKey) ?? this.defaultTimeInterval)
          const nameQueryParam = constructQueryParameterName('name', this.completedCallQueryPrefix)
          const callNameQuery = queryParamMap.has(nameQueryParam) ? queryParamMap.get(nameQueryParam) : null

          const { sortField, sortDirection } = getPaginationOptionsFromQueryParamMap(queryParamMap, this.completedCallQueryPrefix, {
            pageSize: this.completedCallsDefaultPageSize,
            sortField: 'actualStartDate',
            sortDirection: 'desc',
          })
          return this.metricsService.exportCompletedCalls(
            null,
            organizationIds,
            exclusionFilters,
            activeView,
            interval,
            callNameQuery,
            sortField,
            sortDirection ?? 'desc',
            this.showAudioMetrics
          )
        })
      )
      .subscribe()
  }
}
